import { useMemo, useState } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { CP_COACHING } from "@/lib/cp-coaching";
import {
  ALL_QUESTIONS,
  INDUSTRY_KEYS,
  type QuestionDef,
  type QuestionFlag,
  type IndustryKey,
} from "../../../shared/questions";
import { AIP_QUESTION_BANK_VERSION, AIP_SPEC_VERSION } from "@/version";
import { ChevronRight, ChevronDown, Search } from "lucide-react";

// Checkpoint metadata — locked CP10 taxonomy 2026-05-06
// Canonical CP titles — locked 2026-05-26 per helix-checkpoints.ts
const CP_META: Record<number, { title: string; weight: string; subtitle: string }> = {
  1:  { title: "Project",                               weight: "hard-kill",         subtitle: "Real project, named sponsor, budget cycle, why-now" },
  2:  { title: "Need / Pain / Demonstration",           weight: "hard-kill",         subtitle: "Pain named, quantified, in the buyer's own words" },
  3:  { title: "Budget",                               weight: "hard-kill",         subtitle: "Real money, named source, validated line item, owner named" },
  4:  { title: "Timeline",                             weight: "hard-kill",         subtitle: "Decision window AND implementation window — both real" },
  5:  { title: "Authority — Project / Initiative",      weight: "soft",              subtitle: "Direct access to the project or initiative authority" },
  6:  { title: "Technical and Financial Sign-off",      weight: "hard-kill",         subtitle: "Technical evaluation and financial approval gates identified and engaged" },
  7:  { title: "Org Structure and Influence Map",       weight: "soft",              subtitle: "Champion, supporter, influencer, detractor — agendas mapped" },
  8:  { title: "Competitive Position",                  weight: "soft",              subtitle: "Where you stand vs alternatives in buyer's head, evaluated not assumed" },
  9:  { title: "Compliance — IT Internal and Industry-Specific", weight: "hard-kill", subtitle: "Every IT internal and industry-specific compliance requirement mapped" },
  10: { title: "Legal — Contracts, SLAs, Terms",       weight: "hard-kill",         subtitle: "Every barrier to signature understood with a path through each" },
};

const FLAG_STYLE: Record<QuestionFlag, { dot: string; label: string; pillBg: string; pillText: string }> = {
  OK: {
    dot: "bg-emerald-500",
    label: "OK",
    pillBg: "bg-emerald-50 border-emerald-200",
    pillText: "text-emerald-800",
  },
  AMBER: {
    dot: "bg-orange-500",
    label: "Orange",
    pillBg: "bg-orange-50 border-orange-200",
    pillText: "text-yellow-800",
  },
  SOFT_STOP: {
    dot: "bg-orange-600",
    label: "Soft stop",
    pillBg: "bg-orange-50 border-orange-200",
    pillText: "text-orange-800",
  },
  HARD_STOP: {
    dot: "bg-rose-600",
    label: "Hard stop",
    pillBg: "bg-rose-50 border-rose-200",
    pillText: "text-rose-800",
  },
};

export default function QuestionTree() {
  const [openCps, setOpenCps] = useState<Set<number>>(new Set([1, 2, 3]));
  const [openQuestions, setOpenQuestions] = useState<Set<string>>(new Set());
  const [industry, setIndustry] = useState<IndustryKey | "ALL">("ALL");
  const [search, setSearch] = useState("");

  // Filter the catalog
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return ALL_QUESTIONS.filter((q) => {
      // Industry filter: ALL = always-on + every industry; specific = always-on + that industry
      if (industry !== "ALL" && q.industryTrigger && q.industryTrigger !== industry) return false;
      if (term) {
        const hay = (
          q.label +
          " " +
          (q.helpText ?? "") +
          " " +
          q.options.map((o) => o.label).join(" ")
        ).toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [industry, search]);

  // Group by CP
  const grouped = useMemo(() => {
    const map = new Map<number, QuestionDef[]>();
    filtered.forEach((q) => {
      const list = map.get(q.cp) ?? [];
      list.push(q);
      map.set(q.cp, list);
    });
    return [...map.entries()].sort((a, b) => a[0] - b[0]);
  }, [filtered]);

  const totalQuestions = filtered.length;
  const totalOptions = filtered.reduce((acc, q) => acc + q.options.length, 0);

  const toggleCp = (cp: number) => {
    const next = new Set(openCps);
    next.has(cp) ? next.delete(cp) : next.add(cp);
    setOpenCps(next);
  };

  const toggleQuestion = (key: string) => {
    const next = new Set(openQuestions);
    next.has(key) ? next.delete(key) : next.add(key);
    setOpenQuestions(next);
  };

  const expandAll = () => {
    setOpenCps(new Set(grouped.map(([cp]) => cp)));
    setOpenQuestions(new Set(filtered.map((q) => q.key)));
  };

  const collapseAll = () => {
    setOpenCps(new Set());
    setOpenQuestions(new Set());
  };

  return (
    <OperatorShell breadcrumbs={[{ label: "Question Bank", href: "/question-bank" }, { label: "Question Tree" }]} subtitle="Methodology reference · the full Mi6 question bank">
      {/* Header */}
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1.5">
          Question bank · {AIP_QUESTION_BANK_VERSION} · Spec {AIP_SPEC_VERSION}
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">The Mi6 question bank</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Ten checkpoints. The full reference catalog of every question the AE, BDR, and manager walk before the
          methodology calls an account real. Industry branches surface only when their industry is
          selected. Hard-stop options gate progress; soft-stops cap at amber.
        </p>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Stat label="Checkpoints" value={String(grouped.length)} hint="of 10" />
        <Stat label="Questions" value={String(totalQuestions)} hint={industry === "ALL" ? "all surfaces" : `industry: ${industry}`} />
        <Stat label="Answer options" value={String(totalOptions)} hint="canonical chips" />
        <Stat label="Hard kills" value="7" hint="CP 1, 2, 3, 4, 6, 9, 10" />
      </div>

      {/* Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-6 p-3 rounded-md border border-border bg-[hsl(var(--surface))]">
        {/* Industry filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Industry</span>
          <button
            onClick={() => setIndustry("ALL")}
            className={`text-[12px] px-2.5 py-1 rounded border transition-colors ${
              industry === "ALL"
                ? "border-foreground bg-foreground text-background"
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            All
          </button>
          {INDUSTRY_KEYS.map((ind) => (
            <button
              key={ind}
              onClick={() => setIndustry(ind)}
              className={`text-[12px] px-2.5 py-1 rounded border transition-colors ${
                industry === ind
                  ? "border-foreground bg-foreground text-background"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {ind}
            </button>
          ))}
        </div>

        <div className="lg:ml-auto flex items-center gap-2">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search questions or chips"
              className="w-56 lg:w-72 text-[13px] pl-8 pr-3 py-1.5 rounded border border-border bg-background focus:outline-none focus:border-foreground transition-colors"
            />
          </div>
          <button
            onClick={expandAll}
            className="text-[12px] px-2.5 py-1.5 rounded border border-border text-muted-foreground hover:text-foreground transition-colors"
          >
            Expand all
          </button>
          <button
            onClick={collapseAll}
            className="text-[12px] px-2.5 py-1.5 rounded border border-border text-muted-foreground hover:text-foreground transition-colors"
          >
            Collapse all
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-6 text-[12px] text-muted-foreground">
        <span className="text-[11px] uppercase tracking-[0.18em]">Flag legend</span>
        {(Object.keys(FLAG_STYLE) as QuestionFlag[]).map((f) => (
          <span key={f} className="inline-flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${FLAG_STYLE[f].dot}`} />
            {FLAG_STYLE[f].label}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm border border-rose-300 bg-rose-50" />
          hard-kill CP
        </span>
      </div>

      {/* Tree */}
      <div data-tour="question-tree" className="space-y-3">
        {grouped.length === 0 && (
          <div className="text-sm text-muted-foreground border border-dashed border-border rounded-md p-8 text-center">
            No questions match this filter.
          </div>
        )}
        {grouped.map(([cp, questions]) => {
          const meta = CP_META[cp];
          // Hard-kill checkpoints per locked CP10 model: 1, 2, 3, 4, 6, 9, 10
          const isHardKill = [1, 2, 3, 4, 6, 9, 10].includes(cp);
          const isOpen = openCps.has(cp);
          return (
            <div
              key={cp}
              className="border border-border rounded-md bg-[hsl(var(--surface))] overflow-hidden"
            >
              <button
                onClick={() => toggleCp(cp)}
                className="w-full flex items-center gap-3 p-4 text-left hover:bg-[hsl(var(--surface-2))]/50 transition-colors"
              >
                {isOpen ? (
                  <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                )}
                <div
                  className={`flex items-center justify-center w-9 h-9 rounded text-sm font-mono font-semibold flex-shrink-0 ${
                    isHardKill ? "bg-rose-50 text-rose-700 border border-rose-200" : "bg-[hsl(var(--surface-2))] text-foreground"
                  }`}
                >
                  CP{cp}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-[15px] font-semibold tracking-tight">{meta.title}</span>
                    <span className="text-[11px] text-muted-foreground font-mono">{meta.weight}</span>
                    {isHardKill && (
                      <span className="text-[10px] uppercase tracking-wider text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded">
                        hard-kill
                      </span>
                    )}
                  </div>
                  <div className="text-[12px] text-muted-foreground mt-0.5">{meta.subtitle}</div>
                </div>
                <div className="text-[11px] text-muted-foreground font-mono flex-shrink-0">
                  {questions.length} {questions.length === 1 ? "question" : "questions"}
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-border bg-background">
                  {/* Coaching narrative — purpose, where you are in the conversation, on-the-fence, overall impact */}
                  {CP_COACHING[cp] && (
                    <div className="px-4 py-4 bg-[hsl(var(--surface-2))]/40 border-b border-border space-y-3">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                            Coaching · why this checkpoint matters
                          </div>
                          <p className="text-[13px] text-foreground/90 leading-relaxed">
                            {CP_COACHING[cp].purpose}
                          </p>
                        </div>
                        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded border border-neutral-200 bg-neutral-50 text-neutral-900 text-[11px] flex-shrink-0">
                          <span className="text-[9px] uppercase tracking-wider opacity-70">Stage</span>
                          <span className="font-medium">{CP_COACHING[cp].stage}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="rounded border border-border bg-background p-3">
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                            Where you are in the conversation
                          </div>
                          <p className="text-[12px] text-foreground/85 leading-relaxed">
                            {CP_COACHING[cp].inConversation}
                          </p>
                        </div>
                        <div className="rounded border border-orange-200 bg-orange-50/40 p-3">
                          <div className="text-[10px] uppercase tracking-wider text-yellow-800 mb-1">
                            On the fence
                          </div>
                          <p className="text-[12px] text-yellow-900/85 leading-relaxed">
                            {CP_COACHING[cp].onTheFence}
                          </p>
                        </div>
                        <div className={`rounded border p-3 ${CP_COACHING[cp].hardKill ? "border-rose-200 bg-rose-50/40" : "border-border bg-background"}`}>
                          <div className={`text-[10px] uppercase tracking-wider mb-1 ${CP_COACHING[cp].hardKill ? "text-rose-800" : "text-muted-foreground"}`}>
                            Overall impact
                          </div>
                          <p className={`text-[12px] leading-relaxed ${CP_COACHING[cp].hardKill ? "text-rose-900/85" : "text-foreground/85"}`}>
                            {CP_COACHING[cp].overallImpact}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="divide-y divide-border">
                  {questions.map((q, idx) => {
                    const qOpen = openQuestions.has(q.key);
                    const flags = new Set(q.options.map((o) => o.flag));
                    const hasHardStop = flags.has("HARD_STOP");
                    const hasSoftStop = flags.has("SOFT_STOP");
                    return (
                      <div key={q.key}>
                        <button
                          onClick={() => toggleQuestion(q.key)}
                          className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-[hsl(var(--surface-2))]/40 transition-colors"
                        >
                          <span className="text-[11px] font-mono text-muted-foreground flex-shrink-0 mt-0.5 w-6">
                            {String(idx + 1).padStart(2, "0")}
                          </span>
                          {qOpen ? (
                            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 mt-1" />
                          ) : (
                            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 mt-1" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <span className="text-[14px] font-medium">{q.label}</span>
                              {q.industryTrigger && (
                                <span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-[hsl(var(--surface-2))] border border-border px-1.5 py-0.5 rounded">
                                  {q.industryTrigger}
                                </span>
                              )}
                              {hasHardStop && (
                                <span className="text-[10px] uppercase tracking-wider text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded">
                                  hard stop
                                </span>
                              )}
                              {hasSoftStop && !hasHardStop && (
                                <span className="text-[10px] uppercase tracking-wider text-orange-700 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded">
                                  soft stop
                                </span>
                              )}
                              {q.skipOnFastlane && (
                                <span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-[hsl(var(--surface-2))] border border-border px-1.5 py-0.5 rounded">
                                  fastlane skip
                                </span>
                              )}
                            </div>
                            {q.helpText && (
                              <div className="text-[12px] text-muted-foreground mt-1">{q.helpText}</div>
                            )}
                          </div>
                          <div className="text-[11px] text-muted-foreground font-mono flex-shrink-0 mt-0.5">
                            {q.options.length} chips
                          </div>
                        </button>

                        {qOpen && (
                          <div className="ml-12 mr-4 mb-3 border-l border-border pl-4 space-y-1.5">
                            {q.options.map((opt) => {
                              const f = FLAG_STYLE[opt.flag];
                              return (
                                <div
                                  key={opt.key}
                                  className="flex items-center gap-3 py-1.5 text-[13px]"
                                >
                                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${f.dot}`} />
                                  <span className="flex-1 min-w-0">{opt.label}</span>
                                  <span
                                    className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${f.pillBg} ${f.pillText}`}
                                  >
                                    {f.label}
                                  </span>
                                  <span className="text-[11px] font-mono text-muted-foreground w-8 text-right">
                                    {opt.points > 0 ? `+${opt.points}` : opt.points}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer note */}
      <div className="mt-10 text-[12px] text-muted-foreground border-t border-border pt-5">
        Industry sub-questions only render when an account's industry matches. The AE answers with
        canonical chips, "I think" (caps the checkpoint at amber), or "TBD" (creates a hunt-list
        row). The eight always-on questions become baseline; industry adds depth without inflating
        the surface for everyone.
      </div>
    </OperatorShell>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="border border-border rounded-md p-3 bg-[hsl(var(--surface))]">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold tracking-tight mt-1">{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}
